import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { userMaySeeActivitiesImportNav } from "../lib/adminImport";
import {
  fetchAdminNudgesOverview,
  scheduleAdminNudges,
} from "../api/adminNudges";
import { APP_HOME } from "../lib/appRoutes";
import "./AdminNudgesPage.css";

function defaultFireAtLocal() {
  const d = new Date(Date.now() + 2 * 60 * 60 * 1000);
  d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-CH", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function NudgeGroupList({ groups, emptyLabel }) {
  if (!groups?.length) {
    return <p className="admin-nudges-empty">{emptyLabel}</p>;
  }
  return (
    <ul className="admin-nudges-group-list">
      {groups.map((g) => (
        <li key={`${g.fireAt}-${g.title}-${g.count}`} className="admin-nudges-group-item">
          <div className="admin-nudges-group-meta">
            <span className="admin-nudges-group-time">{formatWhen(g.fireAt)}</span>
            <span className="admin-nudges-group-count">
              {g.count} Empfänger{g.count === 1 ? "" : ""}
            </span>
          </div>
          <p className="admin-nudges-group-title">{g.title}</p>
          {g.body ? <p className="admin-nudges-group-body">{g.body}</p> : null}
          <p className="admin-nudges-group-url">
            Link: <code>{g.url || APP_HOME}</code>
          </p>
          {g.lastSentAt ? (
            <p className="admin-nudges-group-sent">
              Zuletzt gesendet: {formatWhen(g.lastSentAt)}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export default function AdminNudgesPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [fireAtLocal, setFireAtLocal] = useState(defaultFireAtLocal);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState(APP_HOME);
  const [audience, setAudience] = useState("all_with_push");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(null);

  const loadOverview = useCallback(async () => {
    setOverviewError(null);
    setOverviewLoading(true);
    try {
      const data = await fetchAdminNudgesOverview();
      setOverview(data);
    } catch (err) {
      setOverview(null);
      setOverviewError(err instanceof Error ? err.message : String(err));
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!loading && user && !userMaySeeActivitiesImportNav(user)) {
      navigate("/mehr", { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user && userMaySeeActivitiesImportNav(user)) {
      loadOverview();
    }
  }, [user, loadOverview]);

  if (loading) {
    return (
      <div className="admin-nudges-page">
        <p className="admin-nudges-muted">Laden…</p>
      </div>
    );
  }

  if (!user || !userMaySeeActivitiesImportNav(user)) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setSubmitting(true);
    try {
      const fireAt = new Date(fireAtLocal);
      if (Number.isNaN(fireAt.getTime())) {
        throw new Error("Ungültiger Zeitpunkt.");
      }
      const result = await scheduleAdminNudges({
        fireAt,
        title,
        body,
        url,
        audience,
      });
      const when = new Date(result.fireAt).toLocaleString("de-CH", {
        dateStyle: "short",
        timeStyle: "short",
      });
      const audienceLabel =
        audience === "all_with_push"
          ? "Nutzer mit aktivierten Erinnerungen"
          : "alle registrierten Konten";
      setMessage(
        `${result.scheduled} Erinnerung(en) geplant für ${when} (${audienceLabel}).`
      );
      await loadOverview();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const totals = overview?.totals;

  return (
    <div className="admin-nudges-page">
      <header className="admin-nudges-header">
        <h2 className="admin-nudges-title">Push-Erinnerungen (Admin)</h2>
        <p className="admin-nudges-lead">
          Nachrichten planen und Übersicht ausstehender Sendungen. Versand über externen
          Cron → <code>/api/dispatch-nudges</code> (Vercel).
        </p>
        <Link to="/mehr" className="admin-nudges-back">
          ← Mehr
        </Link>
      </header>

      <section className="admin-nudges-overview" aria-labelledby="admin-nudges-overview-title">
        <div className="admin-nudges-overview-head">
          <h3 id="admin-nudges-overview-title" className="admin-nudges-section-title">
            Geplant & ausstehend
          </h3>
          <button
            type="button"
            className="admin-nudges-refresh"
            onClick={loadOverview}
            disabled={overviewLoading}
          >
            {overviewLoading ? "Lädt…" : "Aktualisieren"}
          </button>
        </div>

        {overviewError ? (
          <p className="admin-nudges-error">{overviewError}</p>
        ) : null}

        {totals ? (
          <p className="admin-nudges-summary">
            {totals.pendingRows} Zeile(n) ausstehend — {totals.dueRows} fällig jetzt,{" "}
            {totals.plannedRows} geplant — {totals.sentRows} kürzlich gesendet (Stichprobe)
          </p>
        ) : null}

        <h4 className="admin-nudges-subsection">Fällig (wartet auf Cron)</h4>
        <NudgeGroupList
          groups={overview?.due}
          emptyLabel="Keine fälligen Erinnerungen."
        />

        <h4 className="admin-nudges-subsection">Geplant (Zukunft)</h4>
        <NudgeGroupList
          groups={overview?.planned}
          emptyLabel="Keine zukünftigen Erinnerungen."
        />

        <h4 className="admin-nudges-subsection">Kürzlich gesendet</h4>
        <NudgeGroupList
          groups={overview?.recentSent}
          emptyLabel="Noch keine gesendeten Erinnerungen in der Stichprobe."
        />
      </section>

      <form className="admin-nudges-form" onSubmit={handleSubmit}>
        <h3 className="admin-nudges-section-title">Neue Erinnerung planen</h3>

        <label className="admin-nudges-field">
          <span className="admin-nudges-label">Zielgruppe</span>
          <select
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            disabled={submitting}
          >
            <option value="all_with_push">
              Nur Nutzer mit aktivierten Erinnerungen (push_subscriptions)
            </option>
            <option value="all_users">Alle registrierten Konten (auth)</option>
          </select>
        </label>

        <label className="admin-nudges-field">
          <span className="admin-nudges-label">Senden am (lokal)</span>
          <input
            type="datetime-local"
            value={fireAtLocal}
            onChange={(e) => setFireAtLocal(e.target.value)}
            required
            disabled={submitting}
          />
        </label>

        <label className="admin-nudges-field">
          <span className="admin-nudges-label">Titel (Push)</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            required
            placeholder="Kurzer Titel"
            disabled={submitting}
          />
        </label>

        <label className="admin-nudges-field">
          <span className="admin-nudges-label">Text (optional)</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={300}
            rows={3}
            placeholder="Zusätzlicher Text in der Meldung"
            disabled={submitting}
          />
        </label>

        <label className="admin-nudges-field">
          <span className="admin-nudges-label">Link beim Tippen</span>
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="/"
            disabled={submitting}
          />
        </label>

        {error ? <p className="admin-nudges-error">{error}</p> : null}
        {message ? <p className="admin-nudges-success">{message}</p> : null}

        <button type="submit" className="admin-nudges-submit" disabled={submitting}>
          {submitting ? "Wird geplant…" : "Erinnerungen planen"}
        </button>
      </form>
    </div>
  );
}
